import axiosConfig from "./axiosConfig";

// Map-viewport bounding box for a "Search this area" search. Matches the
// shape a Leaflet `map.getBounds()` serialises to; the BE maps it to the
// postcode-district (outcode) set it covers and searches those. Sent
// INSTEAD of `postcodeOutward` - a typed postcode always wins if both are
// somehow present.
export interface LeadSearchViewportBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface LeadSearchRequest {
  // Running a search is what records it in Search history - there is no save
  // action. The name defaults to "<site> (<radius>km, <area>m²+)" and is the
  // search's identity, so re-running under the same name replaces its stored
  // result set. Omitted on a search that should not be recorded.
  name?: string;
  // Opens a recorded search's frozen row set instead of re-running the filter,
  // so the operator returns to exactly what they were reviewing. Sent INSTEAD
  // of any criteria; everything else on this request is ignored.
  savedSearchId?: string;
  // Existing-site buckets only: re-run the bucket's cohort query before reading
  // it back, so the row set reflects the estate as it is now. Opening one no
  // longer refreshes it implicitly (a GET that commits), so this is the explicit
  // ask. Ignored by a backend that predates it, which leaves the open reading
  // the stored set - the same rows, just as of the last refresh.
  refresh?: boolean;
  postcodeOutward?: string;
  minFloorAreaSqm?: number;
  maxFloorAreaSqm?: number;
  radiusKm?: number;
  // Present for a "Search this area" (map-viewport) search. The current
  // floor-area filters still apply; the area comes from the map instead of
  // a typed postcode.
  viewportBounds?: LeadSearchViewportBounds;
  // Bypass the per-row enrichment cache. Clears `*EnrichedAt` on every
  // matching VOA row + re-runs every external lookup (geocode +
  // buildings + HMLR + CH). Always returns `status: "pending"` because
  // a clear is by definition a cold-cache search.
  forceRefetch?: boolean;
  // Two-step UX. First call leaves this unset and the backend returns
  // `status: "preview"` with row counts so the FE can show a confirm
  // modal. Second call passes `confirmEnrichment: true` to actually
  // clear cache + spawn the worker. Searches that hit the all-cached
  // fast path skip the prompt entirely (no external API cost).
  confirmEnrichment?: boolean;
}

export interface HmlrMatch {
  proprietorName: string;
  proprietorAddress: string | null;
  tenure: string | null;
  titleNumber: string | null;
  companyRegistrationNumber: string | null;
  distanceMetres: number | null;
  confidence: number;
  // Raw GeoJSON polygon coords for the title boundary. Optional for
  // backwards compatibility with rows enriched before the field was
  // added. The right panel uses this to filter the HMLR list to deeds
  // overlapping the operator's currently selected building.
  polygon?: number[][][] | number[][][][] | null;
  // Cached server-side bbox: [west, south, east, north].
  bbox?: [number, number, number, number] | null;
  // When the proprietor name fuzzy-matches an existing platform
  // Organisation, the BE attaches `orgId` + `orgName` so the FE can
  // render the cleaner platform name in place of the raw HMLR
  // proprietor (e.g. "B&Q" not "B & Q PROPERTIES LIMITED").
  orgId?: string | null;
  orgName?: string | null;
}

export interface EpcMatch {
  // 20-digit certificate number from the MHCLG non-domestic register.
  // Stable across the legacy + new APIs; replaced the legacy LMK_KEY
  // field name when the Get Energy Performance of Buildings service
  // launched (2026-05).
  certificateNumber: string;
  address1: string | null;
  address2: string | null;
  address3: string | null;
  postcode: string | null;
  totalFloorAreaSqm: number | null;
  // SBEM numeric energy rating (lower is better, 0-150 typical range).
  assetRating: number | null;
  // A-G band derived from `assetRating` (A is best).
  assetRatingBand: string | null;
  propertyType: string | null;
  transactionType: string | null;
  inspectionDate: string | null;
  lodgementDate: string | null;
  localAuthorityLabel: string | null;
  buildingReferenceNumber: string | null;
}

export interface TenantMatch {
  companyName: string;
  // Optional - Companies House matches always have one (the canonical
  // identifier the platform stores on SiteOwnership). Google Places
  // matches don't (Places returns business names, not CH identifiers);
  // the operator can cross-reference manually if they want to.
  companyNumber: string | null;
  companyStatus: string | null;
  sicCodes: string[];
  registeredAddress: string | null;
  confidence: number;
  // Where the match came from. "ch" = Companies House search-by-
  // postcode (broad recall, lots of dormant orgs); "google_places" =
  // Google Places Nearby Search at the building's lat/lon (address-
  // precise, returns the actual operating business). Optional for
  // backwards compat with rows enriched before the Places source was
  // added.
  source?: "ch" | "google_places" | "osm";
  googlePlaceId?: string | null;
  // OSM element id ("node/123" / "way/456") when source=osm. Lets the
  // operator follow the link back to OSM if they want to corroborate.
  osmId?: string | null;
  // The company's website when the source provides one (the OSM
  // `website` tag today). Threaded onto the tenant decision so
  // people-discovery can hand Apollo a precise domain.
  website?: string | null;
  // Distance from the building anchor in metres - populated for
  // Places + OSM matches.
  distanceMetres?: number | null;
  // Business lat/lon. Populated for Places + OSM matches (the FE
  // uses these to render a hover-preview marker on the map). Null
  // for CH (postcode-keyed, no per-company coordinate).
  latitude?: number | null;
  longitude?: number | null;
  // The raw Places `displayName` before our brand-stemming cleanup
  // ran (e.g. "Costco Bristol" before stemming to "Costco"). Only
  // present when stemming changed the name; the FE renders it in the
  // row detail so operators can spot bad stems and report them.
  originalCompanyName?: string | null;
  // When the company name fuzzy-matches an existing platform
  // Organisation, the BE attaches `orgId` + `orgName` so the FE can
  // render the cleaner platform name in place of the raw match
  // value (e.g. "Costco Wholesale" not "Costco" from OSM tags). Same
  // pattern as HmlrMatch.
  orgId?: string | null;
  orgName?: string | null;
}

export interface AgenticCitation {
  url: string;
  title: string;
  publishedDate: string;
  quote: string;
  // R12 evidence tier of this source: "A" registry-grade / "B" owner
  // self-declaration / "C" deal reporting / "D" weak. "" or absent on older runs.
  tier?: string;
}

// R16 ownership chain for one candidate (any field may be blank/absent).
export interface AgenticChainRoles {
  brand?: string;
  manager?: string;
  fund?: string;
  likelyProprietor?: string;
}

// R26: one adjudication line per primed (registry) candidate.
export interface AgenticAdjudication {
  name: string;
  // CONFIRMED / CONTRADICTED / NO_EVIDENCE / NOT_PURSUED, else "".
  status: string;
  note?: string;
}

// R17/R24: a named party that is NOT the landlord, with its role + why-not.
export interface AgenticRelatedParty {
  name: string;
  role: string;
  note?: string;
}

// The R24 decision block + R26 lists of a landlord-research run. Every field is
// optional - an older-shape answer (candidates only) has no decision block.
export interface AgenticDecision {
  // PROCEED / PROCEED_WITH_CAUTION / WITHHELD_AT_SOURCE / INSUFFICIENT /
  // NOT_ESTABLISHED, else "".
  verdict?: string;
  decisionLine?: string;
  partyToContact?: string;
  nextAction?: string;
  adjudications?: AgenticAdjudication[];
  relatedParties?: AgenticRelatedParty[];
  unreachable?: string[];
  notes?: string;
}

// One agentic landlord-research candidate (design PR #1265). Produced by the
// research agent, not an enrichment source; carries its supporting citations.
// The right-panel default-pick + left-table top-landlord skip agentic rows (they
// read the HMLR pool), but the BE DOES auto-select the top candidate into the
// persisted landlord when its confidence is over 60% - see
// `_auto_pick_landlord_selection`; that path is confidence-keyed, not gated on
// `eligibleForAutoPick`.
export interface AgenticMatch {
  source: "agentic";
  taskKey: string;
  proprietorName: string;
  companyNumber: string | null;
  confidence: number;
  reasoning: string;
  citations: AgenticCitation[];
  // Always false - agentic candidates carry no deed polygon so they never enter
  // the HMLR default-pick filter this flag gates. (Research auto-selection is a
  // separate, confidence-keyed path and does not read this flag.)
  eligibleForAutoPick: boolean;
  promptRevision: number;
  // Which generation of the SHIPPED default prompt this run's prompt belongs to.
  // Needed alongside promptRevision because a saved revision superseded by a newer
  // shipped seed resolves back to the seed, which reports as revision 1 - so the
  // revision number alone cannot tell two seed generations apart. Absent on runs
  // from before the stamp existed.
  promptSeedVersion?: number;
  researchedAt?: string | null;
  // Attached by the BE when the proprietor fuzzy-matches a platform Org.
  orgId?: string | null;
  orgName?: string | null;
  // R18 match granularity ("unit"/"estate"/"sole-building"/"estate-partial"/
  // "street"/"town", "" if unstated), R26 not-on-primed-list flag, and R16
  // ownership-chain roles. Absent on older runs.
  matchGranularity?: string;
  notOnList?: boolean;
  chainRoles?: AgenticChainRoles;
  // Set by the BE when this candidate came from an EARLIER run and was carried
  // over because it is the operator's selected landlord and the latest run didn't
  // return it (`_preserve_selected_candidate`). Its confidence / citations /
  // researchedAt are the older run's, so the panel labels the row rather than
  // letting it read as a fresh finding.
  carriedForward?: boolean;
}

// Banded commercial view of a row's tenant, for outreach prioritisation.
// Derived on read by the BE from cached Companies House + Google Maps lookups
// (domain/services/tenant_commercial.py), never persisted, so the bands can be
// re-cut without a migration. Null until the commercial pass has run for this
// row's tenant - render that as an explicit "Unknown", never as a blank.
export interface TenantCommercial {
  companyName: string | null;
  companyNumber: string | null;
  // Companies House statutory accounts band: 'large' | 'medium' | 'small' |
  // 'micro' | 'dormant' | 'unknown'. A legal threshold test, not a headcount.
  sizeBand: string;
  // "Calculating" while `sizePending`, so the label never has to be read
  // alongside the flag to be understood.
  sizeLabel: string;
  // True when the Companies House lookup has not RUN yet, as opposed to having
  // run and found nothing (both leave sizeBand 'unknown'). A tenant with no
  // company number is never pending: there is no key to look up.
  sizePending: boolean;
  accountsType: string | null;
  // End of the accounting period the size band describes. A private company
  // files up to ~9 months after year end, so this can be well over a year old -
  // shown in the tooltip so a stale band reads as stale, not wrong.
  accountsPeriodEndOn: string | null;
  companyStatus: string | null;
  incorporatedOn: string | null;
  // The REGISTERED company the size was read from, when that is not what the
  // row is labelled. A tenant called "Farmfoods Bristol" is sized on FARMFOODS
  // LIMITED, found by searching Companies House for the resolved trading name.
  // Null when the two agree. Show it: an unexplained band on a differently
  // named row is the same unreadable jump as `footprintSearchedAs` fixes.
  sizeCompanyName: string | null;
  // The name Companies House was SEARCHED for - the registered name the identity
  // step resolved ("Lidl Great Britain"), not the row's label and not the Maps
  // trading name. Present whenever a search ran, INCLUDING when it found nothing,
  // which is where it matters most: it is the only way an operator can tell a
  // company we could not size from a search that asked the wrong question.
  // Null when this row's number came from its own postcode match instead.
  sizeSearchedAs: string | null;
  // Google Maps footprint: 'fifty_plus' | 'eleven_to_forty_nine' |
  // 'five_to_ten' | 'one_to_four' | 'none' | 'unknown'.
  footprintBand: string;
  // Operator-facing count: "60+ sites" when the lookup hit the API's 60-result
  // ceiling (an exact "at least 60", not an uncertain one), else the precise
  // number ("23 sites" / "1 site" / "None found" / "Unknown"). The LABEL is the
  // exact count; `footprintBand` is the band the value rule uses.
  footprintLabel: string;
  // True when the Google Maps lookup has not RUN yet. Same distinction as
  // `sizePending`: pending is not a finding.
  footprintPending: boolean;
  // Count of LISTINGS, not sites. Null when the lookup never completed, which
  // is deliberately different from 0 (looked, found none).
  listingCount: number | null;
  // What the Places walk returned before listings sharing a street address were
  // folded into one site. Equal to listingCount unless something collapsed.
  rawListingCount: number | null;
  // Set when the count should not be taken at face value:
  // 'weak_name_match' | 'no_listings_for_trading_company'. Drives the amber
  // hazard icon; `footprintHazardDetail` is the tooltip copy.
  footprintHazard: string | null;
  footprintHazardDetail: string | null;
  // The name the listings were counted under, which is the resolved trading
  // name ("Lidl") rather than the tenant's own label ("Lidl Avonmouth Regional
  // Distribution Centre"). Null when no count was made. Show it: a site count
  // on a row named after one building is otherwise unreadable.
  footprintSearchedAs: string | null;
  // Run id of the AI trace for the call that decided what company this label
  // names - the step the trading name, the site count and (through the Companies
  // House name search) the size band all descend from, so it is the first place
  // to look when the tenant block reads wrong. Null when no model call was made
  // (an operator-supplied domain), or when it was made before traces were kept.
  identityTraceRunId: string | null;
  // The domain in play for this tenant and where it came from ("operator" when a
  // human typed it, "ai" when the identity call resolved it). Null when neither.
  // Worth showing: the site count is searched under the TRADING NAME, so a block
  // that resolves a domain and never says what it got reads as if it ignored it.
  domain: string | null;
  domainSource: string | null;
  // Machine keys for the filing signals, e.g. 'insolvency_history',
  // 'accounts_overdue'. `riskLabels` is the matching operator-facing copy.
  // These are FILING signals, not a credit assessment - Companies House
  // publishes no credit score and no bureau is wired.
  riskFlags: string[];
  riskLabels: string[];
}

// How valuable a lead is to pursue. Deliberately separate from
// TenantCommercial: the tenant profile is its only input today, but building
// size, landlord match quality and prior contact are intended to feed it too
// (BE: domain/services/lead_value.py). Also distinct from `leadConfidence`,
// which answers "did we match the right parties", not "is this worth working".
export interface LeadValue {
  // 'high' | 'medium' | 'low' | 'unknown' | 'pending'. Unknown is a SETTLED
  // verdict (we looked and there is nothing to go on); 'pending' means the
  // lookups are still running. They must stay distinguishable or the operator
  // cannot tell working from done.
  leadValue: string;
  leadValueLabel: string;
  // Why it landed in that band, for the hover tooltip.
  leadValueReason: string;
  // Sort weight, high to low: unknown at 0 and pending below it at -1. Sorting
  // on this rather than on the label keeps the BE the single source of ordering.
  leadValueSort: number;
}

export interface LeadCandidate {
  source: string;
  sourceRef: string;
  address: string | null;
  postcode: string | null;
  postcodeOutward: string | null;
  latitude: number | null;
  longitude: number | null;
  // Which geocoder produced (latitude, longitude). 'mapbox' (HIGH /
  // MEDIUM) is address-precise; 'postcodes_io' (POSTCODE_CENTROID) is
  // the postcode centroid fallback.
  geocodeSource: string | null;
  geocodeConfidence: string | null;
  // Full per-source attempt list (mapbox, google_address_validation,
  // google_places, postcodes_io). One of them has wasPicked=true. Used
  // by the right panel to show how much agreement there was between
  // sources, so the operator can weigh the building-distance metric.
  geocodeAttempts: GeocodeAttempt[] | null;
  floorAreaSqm: number | null;
  rateableValue: number | null;
  primaryDescription: string | null;
  scatDescription: string | null;
  hmlrMatches: HmlrMatch[];
  tenantMatches: TenantMatch[];
  // EPC non-domestic register hits at this row's postcode (most-recent
  // inspection first). Used by the row detail panel for floor-area
  // corroboration + energy-rating display. Each entry's shape is
  // documented in domain/services/enrichment/epc.py on the BE side.
  epcMatches: EpcMatch[];
  // Size + UK footprint of this row's tenant. See TenantCommercial.
  tenantCommercial: TenantCommercial | null;
  // The value band the results table sorts on. See LeadValue.
  leadValue: LeadValue | null;
  // Agentic landlord-research candidates. Empty until an operator runs
  // "Research landlord" on the row; rendered as a distinct source in the
  // landlord options table, never auto-selected. See AgenticMatch.
  agenticMatches: AgenticMatch[];
  // AI-trace run_id of the most recent landlord-research run on this row, so the
  // right panel opens that run's trace from the row alone (null until a run has
  // completed). The lead inbox carries the equivalent in `agenticData.traceRunId`.
  agenticTraceRunId: string | null;
  // The R24 decision block + R26 lists of the most recent research run (verdict,
  // decision line, adjudications, related parties, unreachable). null until R24/R26
  // land a decision; the lead inbox carries the equivalent in `agenticData.decision`.
  agenticDecision?: AgenticDecision | null;
  // Building footprint resolved from datasets-api around the postcode-centroid.
  // When present, the map pin should sit on (buildingLatitude, buildingLongitude)
  // and the polygon outline should render. buildingPolygon coords are GeoJSON
  // [[[lon, lat], ...]] (Polygon) or [[[[lon, lat], ...]]] (MultiPolygon).
  buildingPolygon: number[][][] | number[][][][] | null;
  buildingLatitude: number | null;
  buildingLongitude: number | null;
  buildingSurfaceAreaSqm: number | null;
  buildingDistanceMetres: number | null;
  // Top-N polygon candidates ranked best-first with score breakdown.
  // `buildingCandidates[0]` mirrors the chosen building above; runners-up
  // let the operator validate why polygon #1 was picked.
  buildingCandidates: BuildingCandidate[] | null;
  matchConfidence: number | null;
  existingLeadId: string | null;
  // Set when the resolved building polygon overlaps a site already on
  // the platform. Operator should NOT onboard this lead unless the
  // platform site is being re-platformed. Capitalised `ID` to match
  // the rest of the codebase's ID suffix convention.
  matchedExistingSiteID: string | null;
  matchedExistingSiteName: string | null;
  // VOA-side review state. Edits on /leads/search write these onto
  // the VOA row; promotion to a Lead happens explicitly per
  // sourceRef. UI status derives from these:
  //   existingLeadId -> 'Already a lead'
  //   rejectedAt     -> 'Rejected'
  //   reviewedAt     -> 'Reviewed'
  //   else           -> 'New'
  selectedBuildingBkey: string | null;
  selectedLandlord: SelectedOrg | null;
  selectedTenant: SelectedOrg | null;
  ownerOccupier: boolean;
  // Operator-flagged existing rooftop solar on this row's building, carried into
  // the lead at promotion. False means "not flagged", never "no solar".
  hasPv: boolean;
  // The DERIVED half of `hasPv`: the site's declared existing array in kWp,
  // set only on an existing-sites bucket, where the row already IS a platform
  // site (a VOA row has no site behind it and leaves it null). Feeds the
  // panel's existing `siteExistingPvKwp` prop - the same number the inbox
  // passes from `siteExistingPvKwpByLeadId`, off the same backend helper, so a
  // row reads the same before and after promotion.
  siteExistingPvKwp: number | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  rejectedAt: string | null;
  rejectedBy: string | null;
  // When each enrichment pass last hit upstream for this row.
  // Surfaced on the right panel so the operator can see the data
  // freshness per source.
  geocodedAt: string | null;
  buildingResolvedAt: string | null;
  hmlrEnrichedAt: string | null;
  tenantEnrichedAt: string | null;
  // Set whenever the EPC pass has run for this row (whether or not it
  // returned any certs at the postcode).
  epcEnrichedAt: string | null;
  // Set when the agentic landlord-research agent has been run on this row.
  // Null = never researched; drives the "Research landlord" button's
  // researched/not-researched state.
  agenticResearchedAt: string | null;
  // The operationKey of a landlord-research run (single or bulk) that is LIVE for this
  // row right now, else null. Server-derived per response, so any surface rendering the
  // row shows the true state: the launching page's in-memory handle is lost by
  // navigating away, searching another area, or opening a second tab.
  researchOperationKey: string | null;
  // Set when the paid Google Places passes have been run on this row
  // (operator-opt-in via /leads/voa-premium-enrich). Null = default
  // cheap-path data only; the per-row "Enrich with paid APIs" button
  // hides itself once this is set.
  premiumEnrichedAt: string | null;
  // Sources for which the most recent enrichment attempt was
  // prevented (circuit breaker open, transport error, 5xx, 429).
  // Empty list = clean run; missing/null = unknown (row was last
  // enriched before this column landed). Possible values:
  // "overpass", "companies_house". The FE surfaces this as a per-
  // row badge on /leads/search and offers a bulk refetch-degraded
  // action so the operator can re-run only the missing sources.
  enrichmentDegradedSources: string[];
}

export interface SelectedOrg {
  orgId?: string | null;
  displayName: string;
  // `google_places` was added when Places became a co-equal tenant
  // resolver; `osm` was added when OpenStreetMap Overpass was wired
  // in as a free Stage-1 source. The BE stores the selection as
  // JSONB and doesn't validate the enum, so adding a new value is
  // FE-only.
  source:
    | "hmlr"
    | "ch"
    | "google_places"
    | "osm"
    | "platform"
    | "manual"
    | "agentic";
  hmlrTitleNumber?: string | null;
  chCompanyNumber?: string | null;
  // Populated only when `source === "google_places"` so the
  // selected-row identity round-trips correctly (Places matches don't
  // have a CH company number, so without this the radio button on
  // the picked row never re-checks after click).
  googlePlaceId?: string | null;
  // Populated only when `source === "osm"`. Same role as
  // `googlePlaceId` for Places matches - lets the saved-row identity
  // survive a panel re-mount.
  osmId?: string | null;
  // The company's domain. Captured automatically for `source === "osm"` (the
  // OSM `website` tag), and typed by the operator in the tenant block when we
  // got it wrong. Persisted on the decision, and it outranks the AI-resolved
  // domain everywhere it is used: people-discovery hands Apollo a precise
  // domain instead of a fuzzy name search, and the commercial pass counts the
  // company's Maps listings under it. One correction, both surfaces.
  website?: string | null;
}

export interface GeocodeAttempt {
  source: string;
  confidenceLabel: string | null;
  lat: number | null;
  lon: number | null;
  wasPicked: boolean;
}

export interface BuildingCandidate {
  // Stable key derived from centroid+area; lets the operator's pick
  // survive re-fetch and lines up with `selectedBuildingBkey` on the
  // VOA row (or `acceptedBkey` on a Lead's polygonDecision).
  bkey: string;
  polygon: number[][][] | number[][][][];
  centroidLat: number;
  centroidLon: number;
  surfaceAreaSqm: number | null;
  distanceMetres: number;
  // Per-candidate 0-100 confidence (distance-weighted blend with the
  // VOA-declared area, matching `LeadCandidate.matchConfidence`). This
  // is the field to render in the UI - `score` is the raw scorer
  // output and isn't on a fixed scale.
  confidence: number | null;
  // Raw scorer output: `area_weight * proximity * area_match`. Range
  // depends on building size (`area_weight = log10(surfaceAreaSqm)`)
  // so don't display this as a percentage; sort with it at most.
  score: number | null;
  proximityWeight: number | null;
  areaWeight: number | null;
  areaMatch: number | null;
  // True when `areaMatch` is below the BE's MIN_AREA_MATCH_WITH_DECLARED_AREA
  // (0.4). The resolver kept the polygon (otherwise the operator wouldn't
  // have anything to pick from) but it sorts after any above-floor peer
  // and its confidence is penalised. Optional for backwards-compat with
  // rows enriched before this flag was added.
  belowAreaMatchFloor?: boolean;
  // True when at least one geocoder anchor landed inside this polygon.
  // Used by the BE as a tier-2 promotion within the below-floor branch
  // ("the pin actually fell on this building" outranks larger-but-distant
  // peers). Optional for backwards-compat.
  pinOverlapsPolygon?: boolean;
  // Per-polygon platform-site overlap. Populated by the BE for every
  // building candidate so the right-panel warning can update as the
  // operator picks a different polygon. `LeadCandidate.matchedExistingSiteID`
  // mirrors the auto-resolved top pick (`buildingCandidates[0]`) for the
  // row-level "On platform" badge.
  matchedExistingSiteID?: string | null;
  matchedExistingSiteName?: string | null;
}

// Three response shapes:
// - "completed": every matched row is already cache-hot and no
//   refetch was requested. Candidates returned inline, no prompt.
// - "preview": the search would trigger external API calls. The FE
//   shows a confirm modal with the cost-relevant counts; on confirm
//   it re-issues the same request with `confirmEnrichment: true`.
// - "pending": worker has been started. The FE polls `/async_status`
//   until completed, then re-issues the search to get the completed
//   shape.
export type LeadSearchResponse =
  | LeadSearchCompletedResponse
  | LeadSearchPreviewResponse
  | LeadSearchPendingResponse;

// The search this run was recorded as, or the saved search that was opened.
// Null when no `name` was supplied, so nothing was recorded.
export interface SavedSearchRef {
  id: string;
  name: string;
}

export interface LeadSearchCompletedResponse {
  status: "completed";
  candidates: LeadCandidate[];
  totalMatched: number;
  totalReturned: number;
  truncated: boolean;
  savedSearch?: SavedSearchRef | null;
  // What the rows are: VOA rating-list entries, or existing platform sites
  // bucketed by roof area. Only set on a saved-search open; a live search is
  // always VOA. The row actions are VOA-keyed, so the review page uses this to
  // say which of them apply.
  sourceKind?: "voa" | "existing_sites";
  // Only on a saved-search open: rows in the frozen set that have since left
  // the published VOA list (for an existing-sites bucket, sites that have since
  // left the not-started cohort). Surfaced rather than letting them vanish
  // silently.
  removedRowCount?: number;
  // The expanded outcode set the SQL filter actually queried (seed
  // outcode + every neighbour within radiusKm). The map renders an
  // outline polygon per outcode so the operator can see the
  // geographic extent of "what we're searching", not just the seed.
  expandedOutcodes: string[];
  // Rows whose persisted enrichment failed Pydantic validation on
  // the BE-side per-row build. The BE Sentry-captures each one and
  // skips it; the FE surfaces the count so operators know to ask
  // engineering to investigate. Empty when every row materialised
  // cleanly.
  failedRowCount?: number;
  failedRowRefs?: string[];
}

export interface LeadSearchPreviewResponse {
  status: "preview";
  totalMatched: number;
  totalReturned: number;
  truncated: boolean;
  // Rows that would hit external APIs once the operator confirms.
  // Equals totalReturned when forceRefetch is set (cache wiped for
  // every matched row); otherwise just the cold-cache subset.
  totalPendingEnrichment: number;
  forceRefetch: boolean;
  expandedOutcodes: string[];
}

export interface LeadSearchPendingResponse {
  status: "pending";
  // `kind` flags whether the BE took the synchronous Lambda path
  // (`sync`, default for searches up to ~2,500 matched rows) or
  // kicked off a Step Functions chunked search (`chunked`, for
  // larger searches that take 30+ min). The FE renders different
  // copy + a "Search Status" link for chunked.
  kind?: "sync" | "chunked";
  operationKey: string;
  siteId: string;
  totalMatched: number;
  totalReturned: number;
  truncated: boolean;
  totalPendingEnrichment: number;
  expandedOutcodes: string[];
}

const postLeadSearch = async (
  authToken: string,
  body: LeadSearchRequest
): Promise<LeadSearchResponse> => {
  const { data } = await axiosConfig.post("/leads/search", body, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return data;
};

export default postLeadSearch;
